import { type ReactNode } from "react";
import { cn } from "~/lib/utils";
import { Button } from "./button";
import { Icon } from "./icons/icon";

interface MapHintProps {
  children: ReactNode;
  /** Called when the dismiss button is clicked */
  onDismiss?: () => void;
  className?: string;
}

/**
 * A pill-shaped hint message for map interactions.
 * Use this for contextual guidance like "Click on the map to add a place".
 */
export function MapHint({ children, onDismiss, className }: MapHintProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "inline-flex items-center gap-1.5 py-1.5 rounded-full bg-primary text-primary-foreground shadow-lg",
        onDismiss ? "pl-4 pr-2" : "px-4",
        className,
      )}
    >
      <span className="text-sm font-medium">{children}</span>
      {onDismiss && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onDismiss}
          className="size-7 rounded-full text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/20"
          aria-label="Dismiss"
        >
          <Icon i="close" className="size-5" />
        </Button>
      )}
    </div>
  );
}
